import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, DownloadIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { downloadTranscript } from '../../utils/transcripts';
import { mediaLink } from '../../utils/deliverables';
import { toDrivePreviewUrl } from '../../utils/drive';
import { EMPTY_DOC, exportTextFromDoc, type TranscriptDoc } from '../../utils/transcriptDoc';
import { fetchDeliverableTranscript, type StoredTranscript } from '../../utils/transcriptStore';
import { TranscriptVersions } from '../../components/TranscriptVersions';
import { TranscriptEditor } from '../../components/TranscriptEditor';
type LoadState = 'loading' | 'ready' | 'error';

/**
 * THE TRANSCRIPT, TO READ — and nothing else (30 Sep 2026).
 *
 * There were two places a client could ask for a change: the project page,
 * by clicking a line beside the player, and this page, by selecting a
 * passage. Tom, having used both: *"we do now have two ways to make changes
 * - it should be one or the other surely? … Lets go with whichever one is
 * the most user-friendly and reliable."*
 *
 * The project page won, and it is not close. The client is already there,
 * the player and the words are on one screen, and asking is one click, one
 * box, one send — no navigation, no text selection, no editor. It is also
 * the simpler mechanism, so there is less of it to go wrong.
 *
 * So this page is now read-only: play it, read it, download it. What went
 * with that decision, stated plainly rather than quietly dropped:
 *
 * - **A client can no longer edit the transcript's wording.** Tracked
 *   deletions, restore and Save are gone from the client's side. In three
 *   weeks nobody had used them, and they were a second thing that looked
 *   like feedback but was not. Correcting a transcript is the studio's job
 *   now, by re-uploading it.
 * - **Comments on this page are gone too**, for the same reason: they
 *   landed somewhere different from every other piece of client feedback.
 *
 * NO SIGNPOST BACK. A line reading "To ask for a change, click a line on
 * <project>" was added with this and taken straight out again — Tom, on
 * sight of it: *"remove this"*. A page that has nothing to send does not
 * need to explain itself; the back link at the top is the whole answer,
 * and instructions a client did not ask for are just more to read.
 *
 * The changes a client HAS asked for are shown here, so the page they read
 * the words on also tells them what they have already said.
 *
 * THE PLAYER IS PINNED. The film or podcast comes from `transcriptFor`, set
 * in the studio; a film carrying its own transcript is its own source. The
 * header above is a plain block, not fixed, so `sticky top-0` pins the
 * player against the viewport once the header scrolls away — checked
 * against ClientShell rather than assumed.
 */
export function Transcript() {
  const {
    activeProject,
    activeDeliverable,
    clientDeliverables,
    goClient
  } = usePortal();
  const [load, setLoad] = useState<LoadState>('loading');
  const [stored, setStored] = useState<StoredTranscript | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const deliverableKey = activeDeliverable?.id ?? '';
  const projectSlug = activeProject.slug;

  /**
   * The film or podcast these words belong to.
   *
   * A Transcript item says so itself (`transcriptFor`). A film that carries
   * its transcript on its own row is its own source. Anything unattached has
   * none, and the page shows no player rather than guessing — see the note
   * on `transcriptFor` in types/portal.ts for why guessing from titles was
   * abandoned.
   */
  const source = useMemo(() => {
    if (!activeDeliverable) return undefined;
    if (activeDeliverable.type !== 'Transcript') return activeDeliverable;
    const key = (activeDeliverable.transcriptFor ?? '').trim();
    if (!key) return undefined;
    return clientDeliverables.find(entry => entry.id === key);
  }, [activeDeliverable, clientDeliverables]);
  const sourceEmbed = source ? toDrivePreviewUrl(mediaLink(source)) : '';
  const sourceIsAudio = source?.type === 'Audio';

  /** Requests already raised against the cut being reviewed. */
  const raised = useMemo(() => (source?.changeRequests ?? []).filter(request => !source?.version || request.version === source.version), [source]);

  /* Read the authoritative transcript whenever the open deliverable changes. */
  useEffect(() => {
    if (!deliverableKey || !projectSlug) return;
    let live = true;
    setLoad('loading');
    void fetchDeliverableTranscript(projectSlug, deliverableKey).then(result => {
      if (!live) return;
      if (result.status === 'error') {
        /* A failed read is never presented as an empty transcript. */
        setLoad('error');
        return;
      }
      setStored(result.transcript);
      setSelectedId(null);
      setLoad('ready');
    });
    return () => {
      live = false;
    };
  }, [projectSlug, deliverableKey]);
  const saved = stored?.text ?? '';
  const savedDoc: TranscriptDoc = stored?.doc ?? EMPTY_DOC;
  const revisions = stored?.revisions ?? [];
  const latest = revisions[revisions.length - 1];
  const selected = revisions.find(entry => entry.id === selectedId) ?? latest;
  const viewingLatest = !selected || selected.id === latest?.id;
  /* Rebuilds the reading view when a different saved state is chosen. */
  const resetKey = selected ? revisions.indexOf(selected) + 1 : 0;
  if (!activeDeliverable) {
    return <p className="label m-0 text-mute" data-id="element-2195" data-mp="21tiaiq7seu7c">This transcript is no longer available.</p>;
  }
  const item = activeDeliverable;

  /**
   * The player, pinned. Audio gets Drive's own waveform player at the height
   * AudioMaster settled on — 176px, because the frame is cross-origin, Drive
   * renders two different players, and too short crops the taller one at
   * both ends. A film is shown small and to one side: this page is for the
   * words, and a 16:9 frame pinned across the top would leave no room for
   * them.
   */
  const player = sourceEmbed ? <div className="sticky top-0 z-30 max-w-4xl border-b border-rule bg-paper/95 py-5 backdrop-blur" data-id="element-2196" data-mp="2e3q2jpor832t">
      <div className={sourceIsAudio ? 'w-full' : 'w-full sm:w-[22rem]'} data-id="element-2197" data-mp="29crhydwqgitr">
        <div className={sourceIsAudio ? 'h-44 w-full overflow-hidden border border-rule bg-paperDeep' : 'aspect-video w-full overflow-hidden bg-ink'} data-id="element-2198" data-mp="24eg6xzupc29y">
          <iframe title={source?.name ?? 'Player'} src={sourceEmbed} allow={sourceIsAudio ? 'autoplay' : 'autoplay; fullscreen'} allowFullScreen={!sourceIsAudio} className="h-full w-full border-0" data-id="element-2199" data-mp="1vdiduclsxf65" />
        </div>
      </div>
      <p className="label-sm mt-3 m-0 text-mute" data-id="element-2200" data-mp="3kt804ezo9dkb">{source?.name}</p>
    </div> : null;
  return <div data-id="element-2201" data-mp="3ft3kssai2ovo">
      <button type="button" onClick={() => goClient('home')} className="label group flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-2202" data-mp="3ftbxok6mhu6v">
        <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-2203" data-mp="3sb5x5k0o2w6n" />
        Back to {activeProject.name}
      </button>

      <h1 className="mt-10 max-w-3xl font-display text-4xl leading-[1.02] tracking-tight sm:text-6xl" data-id="element-2204" data-mp="5w3bd0jo4b0e">
        {item.name}
      </h1>

      {/* Listen and read on one screen. Placed above everything else on the
          page so it is the thing that stays. */}
      <div className="mt-8" data-id="element-2205" data-mp="3ebwr3d66dige">{player}</div>

      {load === 'loading' ? <p className="mt-12 max-w-4xl font-display text-xl text-mute" data-id="element-2206" data-mp="3jbj4bmm06yp7">Loading transcript…</p> : load === 'error' ? <div className="mt-12 max-w-4xl border border-rule bg-paperDeep px-6 py-8 sm:px-9" data-id="element-2207" data-mp="tmqlztkyxeqn">
          <p className="m-0 font-display text-xl text-charcoal" data-id="element-2208" data-mp="yksf77c3yufu">
            The transcript could not be loaded.
          </p>
          <p className="label-sm mt-4 text-mute" data-id="element-2209" data-mp="1jqdy55lvfynw">Please try again, or contact Portal.</p>
        </div> : !saved ? <div className="mt-12 max-w-4xl border border-rule bg-paperDeep px-6 py-8 sm:px-9" data-id="element-2210" data-mp="1es2s75yp68ay">
          <p className="m-0 font-display text-xl text-charcoal" data-id="element-2211" data-mp="1tgqiawprxpti">
            The transcript has not been added yet.
          </p>
          <p className="label-sm mt-4 text-mute" data-id="element-2212" data-mp="1ommegmbt1xwn">
            We will let you know as soon as it is ready to read.
          </p>
        </div> : <>
          <div className="mt-8" data-id="element-2213" data-mp="1ydp9p7i1zio0">
            <button type="button" onClick={() => downloadTranscript(item.name, exportTextFromDoc(savedDoc))} className="label flex items-center gap-3 border border-rule px-7 py-4 text-graphite transition-colors duration-150 ease-out hover:border-ink hover:text-ink" data-id="element-2214" data-mp="2ce03ojentdde">
              Download
              <DownloadIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-2215" data-mp="d1xkzbdv0n0s" />
            </button>
          </div>

          {/* What has already been asked for on this cut, so the page the
              client reads the words on also shows what they have said. */}
          {raised.length > 0 && <div className="mt-10 max-w-4xl" data-id="element-2216" data-mp="2exeowgurz0l3">
              <p className="label m-0 text-mute" data-id="element-2217" data-mp="2ewlnlvel53ac">Changes you have asked for</p>
              <ul className="m-0 mt-4 list-none border-t border-rule p-0" data-id="element-2218" data-mp="2jvpxypd8x729">
                {raised.map(request => <li key={request.id} className="flex gap-6 border-b border-rule py-4" data-id="element-2219" data-mp="26049qkeu6raw">
                    <span className="label w-20 shrink-0 pt-1 tabular-nums text-mute" data-id="element-2220" data-mp="2aurr7zbfalfz">
                      {request.timecode || '—'}
                    </span>
                    <span className="flex-1 whitespace-pre-line text-[0.98rem] leading-relaxed" data-id="element-2221" data-mp="20zq4fa4mdvgk">
                      {request.body}
                    </span>
                  </li>)}
              </ul>
            </div>}

          <div className="mt-10 max-w-4xl" data-id="element-2222" data-mp="3vabrb11slpjg">
            <TranscriptVersions revisions={revisions} selectedId={selected?.id ?? ''} onSelect={id => setSelectedId(id)} data-id="element-2223" data-mp="ft6w1v0tx3ym" />
          </div>

          <div className="mt-12 max-w-4xl" data-id="element-2224" data-mp="5x3kjyk37sz0">
            <span className="label block text-graphite" data-id="element-2225" data-mp="5txx0nr3wz5e">Transcript</span>
            <div className="mt-5" data-id="element-2226" data-mp="avn823ebxcth">
              {/* Read-only. The editor is reused for its reading view — the
                  timecode in the margin against each paragraph — and with
                  `editable` false it offers no actions at all. */}
              <TranscriptEditor doc={viewingLatest ? savedDoc : selected?.doc ?? EMPTY_DOC} editable={false} resetKey={resetKey} onChange={() => undefined} comments={[]} canComment={false} onComment={async () => false} cues={viewingLatest ? stored?.cues : undefined} cueText={viewingLatest ? stored?.cueText : undefined} data-id="element-2227" data-mp="oms9nb19cf6g" />
            </div>
          </div>
        </>}
    </div>;
}